import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

export interface CleaningStatusEvent {
  projectId: string;
  projectName: string;
  state: 'in-progress' | 'success' | 'error';
  message?: string;
  currentStep?: number;
  totalSteps?: number;
  goalName?: string;
  columnName?: string;
}

export interface CleaningCompletedEvent {
  projectId: string;
  projectName: string;
  responseData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class CleaningStatusService {
  private statusesSubject = new BehaviorSubject<Map<string, CleaningStatusEvent>>(new Map());
  readonly statuses$: Observable<Map<string, CleaningStatusEvent>> = this.statusesSubject.asObservable();
  private completedSubject = new Subject<CleaningCompletedEvent>();
  readonly completed$: Observable<CleaningCompletedEvent> = this.completedSubject.asObservable();

  // Legacy observable for backward compatibility - emits the first cleaning status or null
  readonly status$: Observable<CleaningStatusEvent | null> = this.statusesSubject.pipe(
    map(statuses => statuses.size > 0 ? Array.from(statuses.values())[0] : null)
  );

  private activeProjects = new Set<string>();
  
  // Store active polling subscriptions so they persist across navigation
  private pollingSubscriptions = new Map<string, Subscription>();

  isProjectCleaning(projectId: string): boolean {
    return this.activeProjects.has(projectId);
  }

  /**
   * Register a polling subscription for a project's cleaning operation.
   * This ensures the subscription persists across navigation.
   */
  registerPollingSubscription(projectId: string, subscription: Subscription): void {
    // Clean up any existing subscription for this project
    const existing = this.pollingSubscriptions.get(projectId);
    if (existing && !existing.closed) {
      existing.unsubscribe();
    }
    this.pollingSubscriptions.set(projectId, subscription);
  }

  /**
   * Get the active polling subscription for a project, if any.
   */
  getPollingSubscription(projectId: string): Subscription | undefined {
    return this.pollingSubscriptions.get(projectId);
  }

  notifyStarted(projectId: string, projectName: string): void {
    this.activeProjects.add(projectId);
    const statuses = new Map(this.statusesSubject.value);
    statuses.set(projectId, { projectId, projectName, state: 'in-progress', message: 'Starting Workflow' });
    this.statusesSubject.next(statuses);
  }

  notifyProgress(projectId: string, projectName: string, currentStep?: number, totalSteps?: number, message?: string, goalName?: string, columnName?: string): void {
    const statuses = new Map(this.statusesSubject.value);
    statuses.set(projectId, { projectId, projectName, state: 'in-progress', message: message ?? 'Workflow in Progress', currentStep, totalSteps, goalName, columnName });
    this.statusesSubject.next(statuses);
  }

  notifyCompleted(projectId: string, projectName: string, responseData?: any): void {
    this.activeProjects.delete(projectId);
    const statuses = new Map(this.statusesSubject.value);
    statuses.set(projectId, { projectId, projectName, state: 'success' });
    this.statusesSubject.next(statuses);
    this.completedSubject.next({ projectId, projectName, responseData });
    
    // Clean up the polling subscription when cleaning completes
    this.cleanupPollingSubscription(projectId);
  }

  notifyFailed(projectId: string, projectName: string): void {
    this.activeProjects.delete(projectId);
    const statuses = new Map(this.statusesSubject.value);
    statuses.set(projectId, { projectId, projectName, state: 'error' });
    this.statusesSubject.next(statuses);
    
    // Clean up the polling subscription when cleaning fails
    this.cleanupPollingSubscription(projectId);
  }

  /**
   * Clean up the polling subscription for a project.
   */
  private cleanupPollingSubscription(projectId: string): void {
    const subscription = this.pollingSubscriptions.get(projectId);
    if (subscription && !subscription.closed) {
      subscription.unsubscribe();
    }
    this.pollingSubscriptions.delete(projectId);
  }

  removeStatus(projectId: string): void {
    const statuses = new Map(this.statusesSubject.value);
    statuses.delete(projectId);
    this.statusesSubject.next(statuses);
  }

  clear(): void {
    this.activeProjects.clear();
    this.statusesSubject.next(new Map());
  }
}
